import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { trim } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useProfileStore } from '../../../../store/storeProfile'

export const PromocodeForm: React.FC = ({ isDialog = false, handleClose }) => {
    const { t } = useTranslation()

    const validationSchema = yup.object({
        code: yup
            .string()
            .trim()
            .matches(
                /^[a-zA-Zа-яА-Я0-9]{1,30}$/,
                t('balanceCard.promocodeValidation'),
            )
            .required(t('balanceCard.enterPromocode')),
    })
    const {
        applyPromocode,
        errorApplyPromocode,
        isLoadingApplyPromocode,
        setIsFetchBalance,
        isApplyPromocode,
        resetErrorApplyPromocode,
    } = useProfileStore((state) => state)
    const [openSnackbar, setOpenSnackbar] = useState(false)

    useEffect(() => {
        if (errorApplyPromocode || isApplyPromocode) {
            setOpenSnackbar(true)
        }
    }, [errorApplyPromocode, isApplyPromocode])

    const handleCloseSnackbar = (event: React.SyntheticEvent | Event, reason?: string) => {
        setOpenSnackbar(false)
        setIsFetchBalance(false)
        resetErrorApplyPromocode()
    }

    const {
        handleSubmit,
        values,
        touched,
        handleBlur,
        handleChange,
        errors,
        isValid,
        dirty,
        resetForm,
    } = useFormik({
        initialValues: {
            code: '',
        },
        validateOnChange: true,
        validationSchema: validationSchema,
        onSubmit: async ({ code }) => {
            await applyPromocode({ code: trim(code, ' ') })
        },
    })

    const size = isDialog ? { xs: 12 } : { xs: 12, sm: 12, md: 6, lg: 6 }
    const sizeButton = isDialog ? { width: '100%' } : { width: { xs: '100%', md: '240px' } }

    return (
        <form onSubmit={handleSubmit}>
            <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                spacing={2}>
                {!isDialog && (
                    <Grid size={12}>
                        <Typography variant="h5">{t('balanceCard.promocode')}</Typography>
                    </Grid>
                )}

                <Grid size={size}>
                    <TextField
                        required
                        margin="normal"
                        fullWidth
                        id="code"
                        name="code"
                        placeholder={t('balanceCard.promocode')}
                        value={values.code}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.code && Boolean(errors.code)}
                        helperText={touched.code && errors.code}
                        slotProps={{
                            htmlInput: {
                                maxLength: 30,
                            },
                        }}
                    />
                </Grid>

                <Stack spacing={1} direction="row" sx={{ width: '100%' }}>
                    <Button fullWidth onClick={handleClose} variant="outlined" color="primary">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        fullWidth
                        disabled={!(isValid && dirty) || isLoadingApplyPromocode}
                        type="submit"
                        variant="contained"
                        sx={sizeButton}>
                        {t('balanceCard.apply')}
                    </Button>
                </Stack>
                {openSnackbar && (
                    <Snackbar
                        className={errorApplyPromocode ? 'snackbar-error' : 'snackbar-success'}
                        open={openSnackbar}
                        autoHideDuration={2000}
                        onClose={handleCloseSnackbar}
                        message={errorApplyPromocode ?? t('balanceCard.promocodeApplied')}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                )}
            </Grid>
        </form>
    )
}
